import { CropType, cropTypes } from '@agroone/entities'

/**
 * Business rules driven by the crop type.
 *
 * This is the single place where "what behaves differently depending on the crop type"
 * lives. Add a rule here (and a usage at the call site reads as `CropTypeRules.xxx(...)`)
 * rather than scattering crop-type checks across the app.
 */

/**
 * Crop types for which the forecast harvest date is optional in the yield estimate form.
 * Sweet corn maturity is driven by week / leaf count, so the date is derived rather than mandatory.
 */
const CROP_TYPES_WITH_OPTIONAL_HARVEST_DATE: ReadonlySet<CropType> = new Set([cropTypes.SWEET_CORN])

/**
 * Control keys that must pass assertion validation before a preharvest sample can be validated,
 * keyed by crop type. Returns null when the crop type has no specific required controls.
 */
const PREHARVEST_REQUIRED_CONTROLS: Partial<Record<CropType, readonly string[]>> = {
  [cropTypes.PEAS]: ['preharvestControl#tend 1'],
  [cropTypes.SWEET_CORN]: [
    'preharvestControl#cob count',
    'preharvestControl#average dry matter',
    'preharvestControl#average moisture',
    'preharvestControl#sample weight',
  ],
}

export const CropTypeRules = {
  /** Whether a forecast harvest date must be provided for the given crop type. */
  isHarvestDateRequired: (cropType: string): boolean => !CROP_TYPES_WITH_OPTIONAL_HARVEST_DATE.has(cropType),

  /**
   * Returns the preharvest control keys that must be validated before a sample can be confirmed,
   * or null if the crop type has no specific requirements.
   */
  getRequiredPreharvestControls: (cropType: string): readonly string[] | null =>
    PREHARVEST_REQUIRED_CONTROLS[cropType as CropType] ?? null,

  /**
   * Whether the bean length and thread controls should be shown (true for all bean types except
   * shelled beans, which shows a "number of stained seeds" control instead).
   */
  hasBeanLengthControl: (cropType: string): boolean => cropType !== cropTypes.SHELLED_BEANS,
} as const
